import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  notification,
  Row,
  Typography,
} from '@duxton-web/ui';

import {
  fromVariableComplexityPolicyDto,
  toVariableComplexityPolicyDto,
} from './api/transform';
import ServiceSelectionModal from './components/ServiceSelectionModal';
import {
  DEFAULT_VARIABLE_COMPLEXITY_POLICY_MODEL,
  VariableComplexityPolicyModel,
} from './model';
import {
  useGetVariableComplexityPolicy,
  useGetVariableComplexityServices,
  useRecomputeVariableComplexityThreshold,
  useUpdateVariableComplexityPolicy,
} from '@/hooks/usePlannerQueries';

const POLICY_FIELDS = [
  'targetCardinalityThreshold',
  'valueSizeThreshold',
  'valueKeysThreshold',
  'valueDepthThreshold',
  'constraintOperatorsThreshold',
  'constraintGroupsThreshold',
  'changeFrequencyThreshold',
] as const;

type PolicyFieldName = (typeof POLICY_FIELDS)[number];
type VariableComplexityPolicyFormValues = Record<PolicyFieldName, string>;

const FIELD_LABELS: Record<PolicyFieldName, string> = {
  targetCardinalityThreshold: 'Target Cardinality Threshold',
  valueSizeThreshold: 'Value Size Threshold',
  valueKeysThreshold: 'Value Keys Threshold (JSON only)',
  valueDepthThreshold: 'Value Depth Threshold (JSON only)',
  constraintOperatorsThreshold: 'Constraint Operators Threshold',
  constraintGroupsThreshold: 'Constraint Groups Threshold',
  changeFrequencyThreshold: 'Change Frequency Threshold',
};

const FIELD_DESCRIPTIONS: Record<PolicyFieldName, string> = {
  targetCardinalityThreshold:
    'Maximum number of rollout targets allowed in a variable.',
  valueSizeThreshold:
    'Maximum allowed payload size for a rollout target value (in bytes).',
  valueKeysThreshold:
    'Maximum number of keys allowed in an object-style rollout target value.',
  valueDepthThreshold:
    'Maximum nesting depth allowed for object or array rollout target values.',
  constraintOperatorsThreshold:
    'Maximum number of constraints allowed in one rollout target.',
  constraintGroupsThreshold:
    'Maximum number of constraint groups allowed in one constraint.',
  changeFrequencyThreshold:
    'Maximum number of changes in the last 60 days for a rollout.',
};

const POLICY_FIELD_CATEGORIES: Array<{
  title: string;
  fields: PolicyFieldName[];
}> = [
  {
    title: 'Target Cardinality',
    fields: ['targetCardinalityThreshold'],
  },
  {
    title: 'Value Complexity',
    fields: ['valueSizeThreshold', 'valueKeysThreshold', 'valueDepthThreshold'],
  },
  {
    title: 'Constraints',
    fields: ['constraintOperatorsThreshold', 'constraintGroupsThreshold'],
  },
  {
    title: 'Change Frequency',
    fields: ['changeFrequencyThreshold'],
  },
];

const DEFAULT_FORM_VALUES: VariableComplexityPolicyFormValues =
  POLICY_FIELDS.reduce((acc, field) => {
    acc[field] = String(DEFAULT_VARIABLE_COMPLEXITY_POLICY_MODEL[field]);
    return acc;
  }, {} as VariableComplexityPolicyFormValues);

const toFormValues = (
  model: VariableComplexityPolicyModel,
): VariableComplexityPolicyFormValues =>
  POLICY_FIELDS.reduce((acc, field) => {
    acc[field] = String(model[field]);
    return acc;
  }, {} as VariableComplexityPolicyFormValues);

const toPolicyModel = (
  values: VariableComplexityPolicyFormValues,
): VariableComplexityPolicyModel =>
  POLICY_FIELDS.reduce((acc, field) => {
    acc[field] = Number(values[field]);
    return acc;
  }, {} as VariableComplexityPolicyModel);

const VariableComplexityPolicyTab = () => {
  const [form] = Form.useForm<VariableComplexityPolicyFormValues>();
  const {
    data: policyDto,
    isLoading,
    error: loadPolicyError,
  } = useGetVariableComplexityPolicy();
  const { isPending: isSaving } = useUpdateVariableComplexityPolicy();

  const { refetch: fetchServices, isFetching: isFetchingServices } =
    useGetVariableComplexityServices();
  const { mutateAsync: recomputeThreshold, isPending: isRecomputing } =
    useRecomputeVariableComplexityThreshold();

  const [modalOpen, setModalOpen] = useState(false);
  const [services, setServices] = useState<string[]>([]);

  useEffect(() => {
    if (!policyDto) return;

    const policy = fromVariableComplexityPolicyDto(policyDto);
    form.setFieldsValue({
      ...DEFAULT_FORM_VALUES,
      ...toFormValues(policy),
    });
  }, [form, policyDto]);

  useEffect(() => {
    if (!loadPolicyError) return;

    notification.error({
      message: 'Failed to load variable complexity policy',
      description:
        loadPolicyError instanceof Error
          ? loadPolicyError.message
          : 'Unexpected error happened.',
    });
  }, [loadPolicyError]);

  const handleSaveAndRecompute = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    try {
      const { data } = await fetchServices();
      setServices(data?.services ?? []);
      setModalOpen(true);
    } catch (err) {
      notification.error({
        message: 'Failed to fetch services',
        description:
          err instanceof Error ? err.message : 'Unexpected error happened.',
      });
    }
  };

  const handleModalOk = async (skipServices: string[]) => {
    const values = form.getFieldsValue();

    try {
      await recomputeThreshold({
        ...toVariableComplexityPolicyDto(toPolicyModel(values)),
        skip_services: skipServices,
      });
      notification.success({
        message: 'Variable complexity recomputed successfully',
      });
      setModalOpen(false);
    } catch (err) {
      notification.error({
        message: 'Failed to recompute variable complexity',
        description:
          err instanceof Error ? err.message : 'Unexpected error happened.',
      });
    }
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        initialValues={DEFAULT_FORM_VALUES}
        disabled={isLoading}
        style={{ height: '100%' }}
      >
        <Flex vertical gap={16} style={{ minHeight: 0, height: '100%' }}>
          <Flex vertical gap={16} style={{ minHeight: 0 }}>
            <Typography.Text type="secondary">
              Configure thresholds for variable complexity policy.
            </Typography.Text>

            {POLICY_FIELD_CATEGORIES.map((category, categoryIndex) => (
              <React.Fragment key={category.title}>
                <Flex vertical gap={8}>
                  <Typography.Text strong>{category.title}</Typography.Text>
                  <Row gutter={16}>
                    {category.fields.map((field) => (
                      <Col key={field} span={8}>
                        <Form.Item
                          label={
                            <Flex vertical gap={2}>
                              <Typography.Text>
                                {FIELD_LABELS[field]}
                              </Typography.Text>
                              <Typography.Text
                                type="secondary"
                                style={{ fontSize: 12 }}
                              >
                                {FIELD_DESCRIPTIONS[field]}
                              </Typography.Text>
                            </Flex>
                          }
                          name={field}
                          rules={[
                            {
                              required: true,
                              message: 'This field is required.',
                            },
                            {
                              pattern: /^\d+$/,
                              message: 'Value must be numeric.',
                            },
                          ]}
                        >
                          <Input
                            style={{ width: '100%' }}
                            inputMode="numeric"
                          />
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>
                </Flex>
                {categoryIndex < POLICY_FIELD_CATEGORIES.length - 1 && (
                  <Divider style={{ margin: '4px 0 8px' }} />
                )}
              </React.Fragment>
            ))}
          </Flex>

          <Flex justify="flex-end" style={{ marginTop: 'auto' }}>
            <Button
              type="primary"
              size="small"
              loading={isFetchingServices || isSaving}
              onClick={handleSaveAndRecompute}
            >
              Save & Recompute Complexity
            </Button>
          </Flex>
        </Flex>
      </Form>

      <ServiceSelectionModal
        open={modalOpen}
        title="Select Services to Recompute"
        services={services}
        confirmLoading={isRecomputing}
        onCancel={() => setModalOpen(false)}
        onOk={handleModalOk}
      />
    </>
  );
};

export default VariableComplexityPolicyTab;
